import { useEffect, useState } from "react";
import type { StoreProduct } from "@/mocks/storeProducts";

interface Props {
  product: StoreProduct;
  onClose: () => void;
  onAdd: (p: StoreProduct, qty: number) => void;
}

const fmt = (n: number) => `KES ${n.toLocaleString()}`;

export default function ProductDetail({ product, onClose, onAdd }: Props) {
  const gallery = [product.image, ...(product.images || [])].filter((v, i, a) => v && a.indexOf(v) === i);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const maxQty = product.stock != null && product.stock !== undefined ? Math.max(1, Number(product.stock)) : 999;

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);

  const wa = `https://wa.me/254724114555?text=${encodeURIComponent(`Hello ISMAN, I'm interested in: ${product.name}${product.sku ? ` (${product.sku})` : ""}`)}`;

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto grid md:grid-cols-2">
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center cursor-pointer" aria-label="Close">
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>
        <div className="bg-gray-50 p-4">
          <div className="aspect-[4/3] rounded-lg overflow-hidden bg-white">
            <img src={gallery[active]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {gallery.map((g, i) => (
                <button key={g} onClick={() => setActive(i)} className={`w-16 h-16 rounded-md overflow-hidden shrink-0 border-2 cursor-pointer ${i === active ? "border-teal-500" : "border-transparent"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 flex flex-col">
          <span className="text-xs text-teal-600 font-semibold uppercase tracking-wider">{product.category}</span>
          <h2 className="text-xl font-extrabold text-gray-900 mt-1">{product.name}</h2>
          {product.sku && <p className="text-[11px] text-gray-400 mt-0.5">SKU: {product.sku}</p>}
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-gray-900">{fmt(product.price)}</span>
            {product.originalPrice && <span className="text-sm text-gray-400 line-through">{fmt(product.originalPrice)}</span>}
            <span className="text-xs text-gray-500">{product.unit}</span>
          </div>
          <p className={`text-xs font-semibold mt-1 ${product.inStock ? "text-emerald-600" : "text-rose-600"}`}>
            <i className={product.inStock ? "ri-checkbox-circle-line" : "ri-close-circle-line"}></i>{" "}
            {product.inStock ? (product.stock != null ? `${product.stock} in stock` : "Available — made to order") : "Out of stock"}
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mt-4">{product.description}</p>
          {product.features.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700"><i className="ri-check-line text-teal-500 mt-0.5"></i>{f}</li>
              ))}
            </ul>
          )}
          <div className="mt-auto pt-6 space-y-3">
            {product.inStock && (
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center cursor-pointer" aria-label="Decrease"><i className="ri-subtract-line"></i></button>
                  <span className="w-10 text-center font-semibold">{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="w-10 h-10 flex items-center justify-center cursor-pointer" aria-label="Increase"><i className="ri-add-line"></i></button>
                </div>
                <button onClick={() => onAdd(product, qty)} className="flex-1 bg-teal-600 hover:bg-teal-500 text-white font-bold py-2.5 rounded-lg cursor-pointer">
                  <i className="ri-shopping-cart-2-line"></i> Add to cart — {fmt(product.price * qty)}
                </button>
              </div>
            )}
            <a href={wa} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 border border-green-500 text-green-700 hover:bg-green-50 font-semibold py-2.5 rounded-lg text-sm">
              <i className="ri-whatsapp-line"></i> Ask about this product on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
